import { useLingui } from "@lingui/react/macro";
import { Maximize2 } from "lucide-react";
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useState,
} from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../base/popover";
import { IconButton } from "../../icon-button";
import { useCloseOnScroll } from "./use-close-on-scroll";

/**
 * A single ellipsized line keeps rows compact and scannable; an expand button
 * opens `children` in a popover with the full content. A blank `line` renders
 * the empty cell, so the button appears only where there is something to
 * expand.
 */
export function CellExpandableLine(props: {
  line?: string | null;
  /** Caps the cell at the column width so long content can't stretch it. */
  style?: CSSProperties;
  /** The popover's sizing and scrolling, which follow the content it holds. */
  popoverClassName?: string;
  /** Where the popover opens; below the button unless set. */
  popoverSide?: ComponentProps<typeof PopoverContent>["side"];
  children: ReactNode;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  useCloseOnScroll(open, setOpen);

  if (!props.line) {
    return <span> </span>;
  }

  return (
    <div
      className="flex min-w-0 items-center gap-1 text-left"
      style={props.style}
    >
      <span className="min-w-0 flex-1 truncate">{props.line}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <IconButton
            icon={Maximize2}
            label={t`View full text`}
            tooltip={null}
            onClick={(e) => e.stopPropagation()}
          />
        </PopoverTrigger>
        <PopoverContent
          side={props.popoverSide}
          align={props.popoverSide === "right" ? "start" : "end"}
          className={props.popoverClassName}
          onFocusOutside={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest("[role=dialog], [role=alertdialog]")
            ) {
              event.preventDefault();
            }
          }}
        >
          {props.children}
        </PopoverContent>
      </Popover>
    </div>
  );
}
